
import { Routes, Route } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster"
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ErrorBoundary } from './components/ErrorBoundary';

import Index from './pages/Index';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContractorDetails from './pages/ContractorDetails';
import BadgeInfo from './pages/BadgeInfo';
import ServicePage from './pages/ServicePage';
import LeadCalculator from './pages/LeadCalculator';
import FeaturedServices from './pages/FeaturedServices';
import ForPros from './pages/ForPros';
import OklahomaCity from './pages/OklahomaCity';
import AdvertiseWithUs from './pages/AdvertiseWithUs';
import NotFound from './pages/NotFound';

// Electrical Service Pages
import ElectricalPage from './pages/services/ElectricalPage';
import BestElectriciansPage from './pages/services/BestElectriciansPage';

// HVAC Service Pages
import BestHVACContractorsPage from './pages/services/BestHVACContractorsPage';

// Roofing Service Pages
import RoofingPage from './pages/services/RoofingPage';
import BestRoofersPage from './pages/services/BestRoofersPage';

// Plumbing Service Pages
import BestPlumbersPage from './pages/services/BestPlumbersPage';

// Restoration Service Pages
import RestorationPage from './pages/services/RestorationPage';
import BestRestorationContractorsPage from './pages/services/BestRestorationContractorsPage';

// General Contractors Pages
import GeneralContractorsPage from './pages/services/GeneralContractorsPage';
import BestGeneralContractorsPage from './pages/services/BestGeneralContractorsPage';

// House Cleaning Pages
import HouseCleaningPage from './pages/services/HouseCleaningPage';
import BestHouseCleaningContractorsPage from './pages/services/BestHouseCleaningContractorsPage';

// Window Cleaning Pages
import WindowCleaningPage from './pages/services/WindowCleaningPage';

// Painting Pages
import PaintingPage from './pages/services/PaintingPage';

// Tree Service Pages
import TreeServicePage from './pages/services/TreeServicePage';
import BestTreeServicesPage from './pages/services/BestTreeServicesPage';

// Pest Control Pages
import BestPestControlPage from './pages/services/BestPestControlPage';
import PestControlPage from './pages/services/PestControlPage';

// Landscaping Pages
import BestLandscapersPage from './pages/services/BestLandscapersPage';
import LandscapingPage from './pages/services/LandscapingPage';

// Admin Pages
import CreateStormDamageBlogPost from './pages/admin/CreateStormDamageBlogPost';

const FallbackUI = () => (
  <div className="text-center py-16 px-4">
    <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Page</h2>
    <p className="mb-4">We're experiencing technical difficulties. Please try refreshing the page.</p>
    <button
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
    >
      Refresh Page
    </button>
  </div>
);

function App() {

  return (
    <div className="min-h-screen bg-background font-sans antialiased flex flex-col">
      <ErrorBoundary fallback={<FallbackUI />}>
        <Header />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={
              <ErrorBoundary fallback={<FallbackUI />}>
                <Index />
              </ErrorBoundary>
            } />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            
            {/* Contractor detail routes with variant handling */}
            <Route path="/:location/:serviceType/:businessSlug" element={<ContractorDetails />} />
            
            {/* Support for old contractor detail URLs */}
            <Route path="/contractor/:contractorId" element={<ContractorDetails />} />
            
            {/* Badge Info */}
            <Route path="/badge-info/:contractorId" element={<BadgeInfo />} />
            
            {/* Service Pages */}
            <Route path="/services/:serviceType" element={<ServicePage />} />
            <Route path="/services/:serviceType/:location" element={<ServicePage />} />
            
            {/* Special Pages */}
            <Route path="/lead-calculator" element={<LeadCalculator />} />
            <Route path="/services" element={<FeaturedServices />} />
            <Route path="/for-pros" element={<ForPros />} />
            <Route path="/advertise-with-us" element={<AdvertiseWithUs />} />
            <Route path="/top-contractors-in-oklahoma-city" element={<OklahomaCity />} />
            <Route path="/hvac" element={<ServicePage />} />
            
            {/* URL Variants for "okc" -> "oklahoma-city" */}
            <Route path="/best-plumbers-okc" element={<BestPlumbersPage />} />
            <Route path="/best-electricians-okc" element={<BestElectriciansPage />} />
            <Route path="/best-hvac-contractors-okc" element={<BestHVACContractorsPage />} />
            <Route path="/best-roofers-okc" element={<BestRoofersPage />} />
            <Route path="/best-general-contractors-okc" element={<BestGeneralContractorsPage />} />
            <Route path="/best-house-cleaners-okc" element={<BestHouseCleaningContractorsPage />} />
            <Route path="/best-painters-okc" element={<ServicePage />} />
            <Route path="/best-restoration-contractors-okc" element={<BestRestorationContractorsPage />} />
            
            {/* Electrical Service Pages */}
            <Route path="/electrical-contractors-oklahoma-city" element={<ElectricalPage />} />
            <Route path="/best-electricians-oklahoma-city" element={<BestElectriciansPage />} />
            
            {/* HVAC Service Pages */}
            <Route path="/best-hvac-contractors-oklahoma-city" element={<BestHVACContractorsPage />} />
            
            {/* Roofing Service Pages */}
            <Route path="/roofing-contractors-oklahoma-city" element={<RoofingPage />} />
            <Route path="/best-roofers-oklahoma-city" element={<BestRoofersPage />} />
            
            {/* Plumbing Service Pages */}
            <Route path="/best-plumbers-oklahoma-city" element={<BestPlumbersPage />} />
            
            {/* Restoration Service Pages */}
            <Route path="/restoration-contractors-oklahoma-city" element={<RestorationPage />} />
            <Route path="/best-restoration-contractors-oklahoma-city" element={<BestRestorationContractorsPage />} />
            
            {/* General Contractors Pages with both path variants */}
            <Route path="/general-contractors-oklahoma-city" element={<GeneralContractorsPage />} />
            <Route path="/best-general-contractors-oklahoma-city" element={<BestGeneralContractorsPage />} />
            
            {/* House Cleaning Pages */}
            <Route path="/house-cleaning-services-oklahoma-city" element={<HouseCleaningPage />} />
            <Route path="/best-house-cleaning-oklahoma-city" element={<BestHouseCleaningContractorsPage />} />
            <Route path="/best-house-cleaners-oklahoma-city" element={<BestHouseCleaningContractorsPage />} />
            
            {/* Window Cleaning Pages */}
            <Route path="/window-cleaning-services-oklahoma-city" element={<WindowCleaningPage />} />
            
            {/* Painting Pages */}
            <Route path="/painting-contractors-oklahoma-city" element={<PaintingPage />} />
            <Route path="/best-painters-oklahoma-city" element={<ServicePage />} />
            
            {/* Tree Service Pages */}
            <Route path="/tree-service-contractors-oklahoma-city" element={<TreeServicePage />} />
            <Route path="/best-tree-services-okc" element={<BestTreeServicesPage />} />
            <Route path="/best-tree-services-oklahoma-city" element={<BestTreeServicesPage />} />
            
            {/* Pest Control Pages */}
            <Route path="/best-pest-control-okc" element={<BestPestControlPage />} />
            <Route path="/best-pest-control-oklahoma-city" element={<BestPestControlPage />} />
            <Route path="/pest-control-services-oklahoma-city" element={<PestControlPage />} />
            
            {/* Landscaping Pages */}
            <Route path="/best-landscapers-okc" element={<BestLandscapersPage />} />
            <Route path="/best-landscapers-oklahoma-city" element={<BestLandscapersPage />} />
            <Route path="/landscaping-services-oklahoma-city" element={<LandscapingPage />} />

            {/* Admin Routes */}
            <Route path="/admin/create-storm-damage-blog-post" element={<CreateStormDamageBlogPost />} />
            
            {/* 404 Page - This must come last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <Toaster />
      </ErrorBoundary>
    </div>
  );
}

export default App;
